// Utility functions for filter operations
import { ActiveFilter, FilterConfig } from './types';
import _ from "lodash";

export const FILTERS_TO_HIDE = [
  'owner',
  'product-area',
  'evidence_createdAt',
  'createdAt',
  'created-at'
]

/**
 * Check if a filter is empty (has no selections)
 */
export const isFilterEmpty = (filter: ActiveFilter): boolean => {
  if (!filter.selected) return true;
  
  if (Array.isArray(filter.selected)) {
    return filter.selected.length === 0;
  }
  
  if (filter.type === 'date' || filter.type === 'date-range') {
    if (typeof filter.selected === 'object' && filter.selected !== null) {
      const dateFilter = filter.selected as any;
      return !dateFilter.values || dateFilter.values.length === 0;
    }
  }
  
  return filter.selected === null || filter.selected === undefined || filter.selected === '';
};

/**
 * Get the count of active filters for a specific category
 * Returns the number of selected options within filters for this category
 * For parent filters, also counts child filters by checking parent path
 */
export const getActiveFilterCountForCategory = (
  categoryId: string,
  localActiveFilters: ActiveFilter[],
  categoryType?: 'idea' | 'evidence'
): number => {
  let count = 0;

  localActiveFilters.forEach((filter: ActiveFilter) => {
    // Skip filters that should be hidden
    if (_.includes(FILTERS_TO_HIDE, filter.name)) {
      return;
    }

    // If categoryType is provided, only match filters with the same filterObject
    if (categoryType && filter.filterObject !== categoryType) {
      return;
    }

    // Match by filter name (categoryId is the filter name like "owner")
    const matchesByName = filter.name === categoryId;
    
    // Match by parent path (for child filters of this category)
    // Parent can be exactly categoryId or start with categoryId + '.'
    const matchesByParent = filter.parent === categoryId || 
      (filter.parent && filter.parent.startsWith(categoryId + '.'));
    
    if (matchesByName || matchesByParent) {
      if (['date', 'custom', 'date-range'].includes(filter.type)) {
        count += 1;
      } else {
        // Count the number of selected options, not just presence of filter
        count += Array.isArray(filter.selected) ? filter.selected.length : (filter.selected ? 1 : 0);
      }
    }
  });
  
  return count;
};

/**
 * Get the count of active selections for a specific child filter
 */
export const getChildFilterActiveCount = (
  childFilterId: string,
  selectedCategory: string,
  activeOptions: { [key: string]: any[] },
  fetchableCheckboxCounts: { [key: string]: number },
  localActiveFilters: ActiveFilter[],
  ideasFiltersConfig: any
): number => {
  if (!selectedCategory || !childFilterId) return 0;
  
  // For direct options (shown as child filters)
  if (childFilterId.startsWith('option-')) {
    const key = selectedCategory;
    const activeOptionsForFilter = activeOptions[key] || [];
    return activeOptionsForFilter.length;
  }
  
  // Check if this is a fetchable checkbox filter
  const isFetchableCheckbox = (() => {
    if (selectedCategory === 'idea') {
      const selectedIdeaData = ideasFiltersConfig?.data?.idea?.[childFilterId];
      return selectedIdeaData?.type === 'fetchableCheckbox';
    } else {
      const selectedCategoryData = ideasFiltersConfig?.data?.evidence?.[selectedCategory];
      if (selectedCategoryData?.childFilters) {
        const childFilterData = selectedCategoryData.childFilters[childFilterId];
        return childFilterData?.type === 'fetchableCheckbox';
      }
    }
    return false;
  })();
  
  if (isFetchableCheckbox) {
    const key = selectedCategory === 'idea' 
      ? `idea-${childFilterId}`
      : `${selectedCategory}-${childFilterId}`;
    return fetchableCheckboxCounts[key] || 0;
  }

  // Check if this is a checkbox filter
  const isCheckbox = (() => {
    if (selectedCategory === 'idea') {
      const selectedIdeaData = ideasFiltersConfig?.data?.idea?.[childFilterId];
      return selectedIdeaData?.type === 'checkbox';
    } else {
      const selectedCategoryData = ideasFiltersConfig?.data?.evidence?.[selectedCategory];
      if (selectedCategoryData?.childFilters) {
        const childFilterData = selectedCategoryData.childFilters[childFilterId];
        return childFilterData?.type === 'checkbox' || childFilterData?.type === 'parent';
      }
    }
    return false;
  })();

  if (isCheckbox) {
    const existingFilter = localActiveFilters.find((filter: ActiveFilter) =>
      filter.name === childFilterId &&
      filter.filterObject === selectedCategory
    );
    return (existingFilter && existingFilter.selected.length) || 0;
  }
  
  // Check if this is a number or boolean filter
  const isNumberOrBoolean = (() => {
    if (selectedCategory === 'idea') {
      const selectedIdeaData = ideasFiltersConfig?.data?.idea?.[childFilterId];
      return selectedIdeaData?.type === 'number' || selectedIdeaData?.type === 'boolean';
    } else {
      const selectedCategoryData = ideasFiltersConfig?.data?.evidence?.[selectedCategory];
      if (selectedCategoryData?.childFilters) {
        const childFilterData = selectedCategoryData.childFilters[childFilterId];
        return childFilterData?.type === 'number' || childFilterData?.type === 'boolean';
      }
    }
    return false;
  })();
  
  if (isNumberOrBoolean) {
    const existingFilter = localActiveFilters.find((filter: ActiveFilter) => 
      filter.name === childFilterId && 
      filter.filterObject === selectedCategory
    );
    return existingFilter && existingFilter.selected !== null && existingFilter.selected !== undefined ? 1 : 0;
  }
  
  // For regular child filters with options
  const key = selectedCategory === 'idea' 
    ? `idea-${childFilterId}`
    : `${selectedCategory}-${childFilterId}`;
  const activeOptionsForFilter = activeOptions[key] || [];
  return activeOptionsForFilter.length;
};

/**
 * Create a consistent key for tracking filter options
 */
export const getFilterKey = (filterObject: string, filterName: string): string => {
  return filterObject === 'idea' 
    ? `idea-${filterName}` 
    : `${filterObject || 'evidence'}-${filterName}`;
};

/**
 * Remove a specific filter from the active filters array
 */
export const removeFilterFromActive = (
  filterName: string,
  localActiveFilters: ActiveFilter[]
): ActiveFilter[] => {
  return localActiveFilters.filter(filter => filter.name !== filterName);
};

/**
 * Update or add a filter in the active filters array
 */
export const updateFilterInActive = (
  filterToUpdate: ActiveFilter,
  localActiveFilters: ActiveFilter[]
): ActiveFilter[] => {
  const existingIndex = localActiveFilters.findIndex(
    filter => filter.name === filterToUpdate.name && filter.filterObject === filterToUpdate.filterObject
  );
  
  if (existingIndex >= 0) {
    const updated = [...localActiveFilters];
    updated[existingIndex] = filterToUpdate;
    return updated;
  } else {
    return [...localActiveFilters, filterToUpdate];
  }
};

/**
 * Check if a filter should be shown in inbox based on showInInbox flag
 * Always returns true for default filters: owner and product-area
 * Returns true if filter has showInInbox: true
 * Returns true if filter is a parent with any child having showInInbox: true
 * Recursively checks child filters
 */
export const shouldShowFilterInInbox = (
  filter: FilterConfig,
  filterId: string,
  filterObject: 'evidence' | 'idea' | 'both'
): boolean => {
  // For 'both' context, include all filters (owner and product-area appear under Idea filters in the modal).
  if (filterObject === 'both') {
    return true;
  }

  if (filterObject === 'evidence') {
    if (filterId === 'owner' || filterId === 'product-area' || filter.showInInbox === true) return true;
  } else {
    // For 'idea' context, show owner and product-area in idea section
    if (filterObject === 'idea' && (filterId === 'owner' || filterId === 'product-area')) {
      return true;
    }
    // Always show default filters (except owner/product-area when not idea)
    if (_.includes(FILTERS_TO_HIDE, filterId)) {
      return false;
    }

    // Check if this filter has showInInbox: true
    if (filter.showInInbox === true || filter.enabled) {
      return true;
    }
  }

  // If this filter has child filters, check if any child should be shown
  if (filter.childFilters && Object.keys(filter.childFilters).length > 0) {
    for (const [childKey, childFilter] of Object.entries(filter.childFilters)) {
      if (shouldShowFilterInInbox(childFilter, childKey, filterObject)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Filter categories based on filterObject type and custom predicates
 */
export const filterCategories = (
  categories: { [key: string]: FilterConfig },
  filterObject: 'evidence' | 'idea' | 'both',
  categoryFilter?: (category: FilterConfig, categoryId: string) => boolean
): { [key: string]: FilterConfig } => {
  const filtered: { [key: string]: FilterConfig } = {};
  
  Object.entries(categories).forEach(([key, category]) => {
    // Skip _id filter
    if (key === '_id') return;
    
    // Apply filterObject filter
    if (filterObject !== 'both' && category.filterObject !== filterObject) {
      return;
    }
    
    // Apply custom filter predicate
    if (categoryFilter && !categoryFilter(category, key)) {
      return;
    }
    
    filtered[key] = category;
  });
  return filtered;
};

export const getManageFiltersObject = (
  filter: FilterConfig,
 
): any => {
 

  return {};
};

// ─── Shared filter-option search helpers ─────────────────────────────────────
// Extracted from CheckboxFilterForModal and CheckboxFilter which both had
// near-identical recursive search implementations.

export interface FilterOption {
  label?: string;
  name?: string;
  value?: string;
  email?: string;
  childComponents?: FilterOption[];
  [key: string]: any;
}

export function filterOptionsByQuery(
  opts: FilterOption[],
  query: string,
  isOwnerFilter = false
): FilterOption[] {
  if (!opts || !Array.isArray(opts)) return [];
  if (!query) return opts;

  const lowerQuery = query.toLowerCase();

  const recurse = (options: FilterOption[]): FilterOption[] =>
    options
      .filter((option:any) => option != null)
      .map((option:any) => {
        const filteredChildren = recurse(option.childComponents || []);
        const labelStr = String(option.label || option.name || '');
        const matchesName = labelStr.toLowerCase().includes(lowerQuery);
        const matchesEmail =
        isOwnerFilter &&
        !!option.email &&
        String(option.email).toLowerCase().includes(lowerQuery);

        if (matchesName || matchesEmail || filteredChildren.length > 0) {
          return { ...option, childComponents: filteredChildren };
        }
        return null;
      })
      .filter((option:any) => option !== null);

  return recurse(opts);
}

// ─── Shared parent/child checkbox selection helpers ──────────────────────────
// Extracted from the three near-identical CheckboxFilter/CheckboxFilterForModal
// implementations (ideas/filters/types, common/filters/FilterTypeRenderers,
// common/filters/components/filter-components-by-types).

function optionValueOf(option: FilterOption): string {
  return String(option?.value || option?.label || '').toLowerCase();
}

function findOptionByValue(optionsList: FilterOption[], value: string): FilterOption | null {
  if (!optionsList || !Array.isArray(optionsList)) return null;
  const needle = (value || '').toLowerCase();
  for (const option of optionsList) {
    if (!option) continue;
    if (optionValueOf(option) === needle) return option;
    if (option.childComponents) {
      const found = findOptionByValue(option.childComponents, value);
      if (found) return found;
    }
  }
  return null;
}

function getAllChildValues(option: FilterOption): string[] {
  if (!option) return [];
  let values: string[] = [];
  if (option.value || option.label) {
    values.push(optionValueOf(option));
  }
  if (Array.isArray(option.childComponents)) {
    for (const child of option.childComponents) {
      if (child) values = values.concat(getAllChildValues(child));
    }
  }
  return values;
}

/**
 * Expand a parent/child checkbox toggle against the full (unfiltered) options
 * tree, then re-derive parent checked state. Callers must pass the unfiltered
 * tree so descendants hidden by search are still selected or cleared.
 */
export function applyParentChildToggle(
  options: FilterOption[],
  selectedValues: string[],
  toggledValue: string | undefined,
  isAdding: boolean
): string[] {
  let result = [...selectedValues];
  const toggledOption = toggledValue ? findOptionByValue(options, toggledValue) : null;
  const hasChildOptions = Boolean(toggledOption?.childComponents?.length);

  if (toggledOption && hasChildOptions) {
    const allValues = getAllChildValues(toggledOption);
    if (isAdding) {
      allValues.forEach((val) => {
        if (!result.includes(val)) result.push(val);
      });
    } else {
      result = result.filter((value) => !allValues.includes(value));
    }
  }

  return syncParentSelection(options, result);
}

/**
 * Derives each parent option's own checked state from its children: a parent
 * with `childComponents` is only "checked" (present in the returned array)
 * when every one of its descendants is checked. Runs bottom-up so multi-level
 * trees resolve correctly. Self-contained — doesn't depend on a caller-supplied
 * child-value collector.
 */
export function syncParentSelection(options: FilterOption[], selectedValues: string[]): string[] {
  let result = selectedValues;

  const visit = (opts: FilterOption[]) => {
    if (!Array.isArray(opts)) return;
    opts.forEach((option) => {
      if (!option?.childComponents?.length) return;
      visit(option.childComponents);
      const descendantValues = option.childComponents.flatMap((child) => getAllChildValues(child));
      const optionValue = optionValueOf(option);
      const allDescendantsSelected =
        descendantValues.length > 0 && descendantValues.every((val) => result.includes(val));
      if (allDescendantsSelected) {
        if (!result.includes(optionValue)) {
          result = [...result, optionValue];
        }
      } else if (result.includes(optionValue)) {
        result = result.filter((val) => val !== optionValue);
      }
    });
  };

  visit(options);
  return result;
}
